import { graphql } from '@@fire-b2b/graphql/codegen'
import type {
  WishlistCountProductsQuery,
  WishlistGroupSummary,
  WishlistSummary,
} from '@@fire-b2b/graphql/codegen/graphql'
import { OrderTypeEnum } from '@@fire-b2b/types/order/OrderTypeEnum'
import type { CartPosition } from '@fire-b2b/composables/cart/cart.types'
import type { MapSizeCopyPaste } from '@fire-b2b/composables/cart/useCartCopyPaste'
import { mapProductSizeGrids } from '@fire-b2b/fragments/ProductSizes'
import { gqlExecute } from '@fire-b2b/utils/gql'
import { defineStore } from 'pinia'

type WishlistGroup = WishlistCountProductsQuery['wishlists']['results'][number]

const WISHLIST_POSITIONS_PRODUCTS_QUERY = graphql(`
  query WishlistCountProducts(
    $loadSubmissions: Boolean
    $pendingOnly: Boolean
    $includeSubCustomers: Boolean
    $shopClientId: [Int]
  ) {
    wishlists(
      loadSubmissions: $loadSubmissions
      pendingOnly: $pendingOnly
      includeSubCustomers: $includeSubCustomers
      shopClientId: $shopClientId
    ) {
      results {
        positions {
          shopProductIdParent
        }
      }
    }
  }
`)

export const useCartStore = defineStore('cart', () => {
  const brandsStore = useBrandsStore()
  const productGroupCountMap = ref<Map<number, number>>(new Map())

  const snackbarStore = useSnackbarStore()
  const t = useTranslate()

  const getProductGroupCount = (shopProductId: number) => {
    return productGroupCountMap.value.get(shopProductId)
  }

  const setProductGroupCount = (shopProductId: number, count: number) => {
    const updated = new Map(productGroupCountMap.value)
    updated.set(shopProductId, count)
    productGroupCountMap.value = updated
  }

  function rebuildBasketProductCountFromPositions(positions: ({ shopProductIdParent?: number | null } | null)[]) {
    const groupMap = new Map<number, number>()
    for (const position of positions) {
      const productId = position?.shopProductIdParent
      if (productId != null) {
        groupMap.set(productId, 1)
      }
    }
    productGroupCountMap.value = groupMap
  }

  const buildProductGroupCount = async (orderType?: string) => {
    const brandId = brandsStore.selectedBrandId
    const resolvedOrderType =
      orderType ?? unwrapOptionalParam(useRouteParams().orderType) ?? brandsStore.defaultOrderType

    if (resolvedOrderType !== OrderTypeEnum.REORDER) {
      await buildWishlistProductGroupCount(brandId)
    }
    // ReOrder badge counts are handled by fetchBasketCount (useBasketCount)
  }

  async function buildWishlistProductGroupCount(brandId?: number) {
    const result = await gqlExecute({
      query: WISHLIST_POSITIONS_PRODUCTS_QUERY,
      variables: {
        loadSubmissions: true,
        pendingOnly: true,
        includeSubCustomers: true,
        shopClientId: brandId ? [brandId] : undefined,
      },
    })

    if (isErr(result)) {
      logErrWarn(result)
      return
    }

    const wishlists: WishlistGroup[] = result.data.wishlists.results ?? []
    const groupMap = new Map<number, number>()

    for (const wishlist of wishlists) {
      for (const position of wishlist.positions ?? []) {
        const productId = position.shopProductIdParent
        if (productId != null) {
          groupMap.set(productId, (groupMap.get(productId) ?? 0) + 1)
        }
      }
    }

    productGroupCountMap.value = groupMap
  }

  // COPY & PASTE
  const COPY_PASTE_STORAGE_KEY = 'cart-copy-paste-data'

  function loadCopyPasteFromSession(): MapSizeCopyPaste {
    return safeJsonParse<MapSizeCopyPaste>(sessionStorage.getItem(COPY_PASTE_STORAGE_KEY)) ?? {}
  }

  const copyPasteData = ref<MapSizeCopyPaste>(loadCopyPasteFromSession())
  const copyPastePreviewOpen = ref(false)

  function resetCopyPasteData() {
    copyPasteData.value = {}
    sessionStorage.removeItem(COPY_PASTE_STORAGE_KEY)
  }

  function setCopyPasteData(data: MapSizeCopyPaste) {
    copyPasteData.value = data
    sessionStorage.setItem(COPY_PASTE_STORAGE_KEY, JSON.stringify(data))
    snackbarStore.show(t('notification.copiedToClipboard'), {
      icon: 'icon-copy',
    })
  }

  // CART STATS
  const cartGroupSummary = ref<WishlistGroupSummary[]>([])
  const cartSummary = ref<WishlistSummary>({})

  function resetCartStats() {
    cartGroupSummary.value = []
    cartSummary.value = {}
  }

  function setCartGroupSummary(summary: WishlistGroupSummary[]) {
    cartGroupSummary.value = summary
  }

  function setCartSummary(summary: WishlistSummary) {
    cartSummary.value = summary
  }

  // CART POSITIONS (shared between PreOrder and ReOrder)
  const positionsById = shallowRef<Record<string, CartPosition>>({})
  const positionOrder = shallowRef<number[]>([])
  type SizeQuantityByPositionId = Record<number, Record<number, string | number>>
  const sizesByPositionId = ref<SizeQuantityByPositionId>({})
  function createCartPositions(data: Record<string, CartPosition>, order: number[]) {
    positionsById.value = data
    positionOrder.value = order
  }
  function createCartSizesByPositionId(data: SizeQuantityByPositionId) {
    sizesByPositionId.value = data
  }

  function removePosition(positionId: number) {
    if (positionsById.value[positionId]) {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete positionsById.value[positionId]
      // call reactivity system to trigger a re-render
      positionsById.value = { ...positionsById.value }
      positionOrder.value = positionOrder.value.filter((id) => id !== positionId)
    }
  }

  function updatePositionSummary(
    positionId: number,
    summary: { subTotal: string; subTotalRrp: string; productQty: number },
  ) {
    const current = positionsById.value[positionId]
    if (!current) {
      return
    }

    positionsById.value = {
      ...positionsById.value,
      [positionId]: {
        ...current,
        subTotal: String(summary.subTotal),
        subTotalRrp: summary.subTotalRrp,
        productQty: summary.productQty,
      },
    }
  }

  function getSizesForPosition(positionId: number) {
    return sizesByPositionId.value[positionId] || {}
  }

  /**
   * Returns the size quantities for the basket position that matches the given shopProductIdParent.
   * Used in CartModalPosition for ReOrder: the position is identified by shopProductIdParent.
   */
  function getQuantitiesByShopProductIdParent(shopProductIdParent: number): Record<number, string | number> {
    for (const posId of positionOrder.value) {
      const pos = positionsById.value[posId]
      if (pos?.shopProductIdParent === shopProductIdParent) {
        return sizesByPositionId.value[posId] || {}
      }
    }
    return {}
  }

  /**
   * Returns the basket position id (basket_position_id) for the given shopProductIdParent.
   * Pass this to updateBasket so the backend updates the existing position instead of creating a new one.
   */
  function getPositionIdByShopProductIdParent(shopProductIdParent: number): number | undefined {
    for (const posId of positionOrder.value) {
      const pos = positionsById.value[posId]
      if (pos?.shopProductIdParent === shopProductIdParent) {
        return posId
      }
    }
    return undefined
  }

  function getPositionByShopProductIdParent(shopProductIdParent: number): CartPosition | undefined {
    for (const posId of positionOrder.value) {
      const pos = positionsById.value[posId]
      if (pos?.shopProductIdParent === shopProductIdParent) {
        return pos
      }
    }
    return undefined
  }

  function updateSizeQuantity(positionId: number, quantity: Record<number, string | number>) {
    if (!sizesByPositionId.value[positionId]) {
      sizesByPositionId.value[positionId] = {}
    }
    sizesByPositionId.value[positionId] = quantity
  }

  function getSizeGrids(positionId: number) {
    const pos = positionsById.value[positionId]
    if (!pos) {
      return []
    }

    return mapProductSizeGrids(pos)
  }

  return {
    buildProductGroupCount,
    setProductGroupCount,
    getProductGroupCount,
    rebuildBasketProductCountFromPositions,
    resetCopyPasteData,
    setCopyPasteData,
    copyPasteData,
    copyPastePreviewOpen,
    cartSummary,
    cartGroupSummary,
    setCartGroupSummary,
    setCartSummary,
    resetCartStats,
    positionsById,
    positionOrder,
    createCartPositions,
    getSizeGrids,
    createCartSizesByPositionId,
    sizesByPositionId,
    getSizesForPosition,
    getQuantitiesByShopProductIdParent,
    getPositionIdByShopProductIdParent,
    getPositionByShopProductIdParent,
    updateSizeQuantity,
    updatePositionSummary,
    removePosition,
  }
})
